import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material'
import {
  FirstPage,
  LastPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material'
import { styled } from '@mui/system'

const PaginationContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

const PaginationButton = styled(IconButton)({
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  width: '32px',
  height: '32px',
  padding: 0,
  fontSize: '12px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&.Mui-disabled': {
    borderColor: '#f0f0f0',
  },
})

const PageNumberButton = styled(IconButton)(({ theme }) => ({
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  width: '32px',
  height: '32px',
  padding: 0,
  fontSize: '12px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&.active': {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    borderColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}))

interface TablePaginationActionsProps {
  count: number
  page: number
  rowsPerPage: number
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void
}

export const PaginatedTablePaginationActions: React.FC<
  TablePaginationActionsProps
> = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleFirstPage = () => onPageChange(null, 0)
  const handlePreviousPage = () => onPageChange(null, page - 1)
  const handleNextPage = () => onPageChange(null, page + 1)
  const handleLastPage = () =>
    onPageChange(null, Math.max(0, Math.ceil(count / rowsPerPage) - 1))

  const isFirstPage = page === 0
  const isLastPage = page >= Math.ceil(count / rowsPerPage) - 1
  const totalPages = Math.ceil(count / rowsPerPage)

  const renderPageNumbers = () => {
    let pages: number[] = []
    if (totalPages <= 10) {
      // Increased from 7 to show more pages directly
      pages = Array.from({ length: totalPages }, (_, i) => i)
    } else {
      if (page < 5) {
        // Show more numbers at start: 1 2 3 4 5 6 7 ... last
        pages = [...Array.from({ length: 7 }, (_, i) => i), -1, totalPages - 1]
      } else if (page > totalPages - 6) {
        // Show more numbers at end: 1 ... n-6 n-5 n-4 n-3 n-2 n-1 n
        pages = [
          0,
          -1,
          ...Array.from({ length: 7 }, (_, i) => totalPages - 7 + i),
        ]
      } else {
        // Show more numbers around current page: 1 ... n-3 n-2 n-1 n n+1 n+2 n+3 ... last
        pages = [
          0, // First page
          -1, // Ellipsis
          page - 3, // Three pages before
          page - 2,
          page - 1,
          page,
          page + 1,
          page + 2,
          page + 3, // Three pages after
          -2, // Ellipsis
          totalPages - 1, // Last page
        ]
      }
    }

    if (isMobile) {
      if (page < 0) {
        return (
          <Box
            sx={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
            }}
          >
            ...
          </Box>
        )
      }

      return (
        <>
          <PageNumberButton
            key={page}
            onClick={() => onPageChange(null, page)}
            className={'active'}
          >
            {page + 1}
          </PageNumberButton>
        </>
      )
    } else {
      return pages.map((pageNum, index) => {
        if (pageNum < 0) {
          return (
            <Box
              key={`ellipsis-${index}`}
              sx={{
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
              }}
            >
              ...
            </Box>
          )
        }

        return (
          <>
            <PageNumberButton
              key={pageNum}
              onClick={() => onPageChange(null, pageNum)}
              className={page === pageNum ? 'active' : ''}
            >
              {pageNum + 1}
            </PageNumberButton>
          </>
        )
      })
    }
  }

  return (
    <PaginationContainer>
      <PaginationButton
        onClick={handleFirstPage}
        disabled={isFirstPage}
        aria-label="first page"
      >
        <FirstPage sx={{ fontSize: 20 }} />
      </PaginationButton>
      <PaginationButton
        onClick={handlePreviousPage}
        disabled={isFirstPage}
        aria-label="previous page"
      >
        <KeyboardArrowLeft sx={{ fontSize: 20 }} />
      </PaginationButton>

      {renderPageNumbers()}

      <PaginationButton
        onClick={handleNextPage}
        disabled={isLastPage}
        aria-label="next page"
      >
        <KeyboardArrowRight sx={{ fontSize: 20 }} />
      </PaginationButton>
      <PaginationButton
        onClick={handleLastPage}
        disabled={isLastPage}
        aria-label="last page"
      >
        <LastPage sx={{ fontSize: 20 }} />
      </PaginationButton>
    </PaginationContainer>
  )
}
